import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import styles from "./HeroCarousel.module.css";

/**
 * HeroCarousel — carrousel d'accueil (section .hero de « 01 - home v5 »).
 *
 * Implémentation propre, sans Swiper : les barres de progression, le
 * ken burns et la parallaxe des maquettes entrent en conflit avec la
 * gestion de transform de Swiper.
 *
 * La forme de slide historique — { id, city, country, img, subtitle } —
 * reste valide : le titre reprend `city`, le sur-titre `country` et la
 * description `subtitle`. Les champs enrichis (eyebrow, titleAccent, chips,
 * prix, remise) sont tous optionnels.
 */

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 5l7 7-7 7" />
  </svg>
);

const IconPause = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="7" y="5" width="3.4" height="14" rx="1" />
    <rect x="13.6" y="5" width="3.4" height="14" rx="1" />
  </svg>
);

const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);

/** Badge de remise : anneau de texte tournant + pourcentage au centre. */
const PromoBadge = ({ discount, label }) => (
  <div className={styles.promoBadge} aria-hidden="true">
    <svg viewBox="0 0 100 100">
      <defs>
        <path
          id="hc-badge-arc"
          d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0"
          fill="none"
        />
      </defs>
      <text>
        <textPath href="#hc-badge-arc" startOffset="0%">
          {`${label} · ${label} · `}
        </textPath>
      </text>
    </svg>
    <b>{discount}</b>
  </div>
);

PromoBadge.propTypes = {
  discount: PropTypes.string.isRequired,
  label: PropTypes.string,
};

export default function HeroCarousel({
  slides,
  autoPlayDelay = 7000,
  onExplore,
  onSecondary,
  children,
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const heroRef = useRef(null);
  const fillRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const touchRef = useRef(null);

  const count = slides?.length || 0;

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const goTo = useCallback(
    (i) => {
      if (!count) return;
      setIndex(((i % count) + count) % count);
    },
    [count]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  /* Barre de progression + avance automatique.
     Une seule boucle rAF pilote les deux : la barre ne peut donc pas se
     désynchroniser du changement de slide. */
  useEffect(() => {
    if (!count || !playing || reduceMotion) return undefined;

    startRef.current = performance.now();
    const bar = fillRef.current;

    const tick = (now) => {
      const ratio = Math.min((now - startRef.current) / autoPlayDelay, 1);
      if (bar) bar.style.width = `${ratio * 100}%`;
      if (ratio >= 1) {
        setIndex((i) => (i + 1) % count);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (bar) bar.style.width = "0%";
    };
  }, [index, playing, count, autoPlayDelay, reduceMotion]);

  /* Onglet masqué : on suspend, sinon la barre rattrape son retard d'un coup
     au retour et le carrousel saute plusieurs slides. */
  useEffect(() => {
    const onVisibility = () => setPlaying(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  /* Parallaxe : la position du curseur alimente --px / --py, le CSS fait
     le reste. */
  const onMouseMove = useCallback(
    (e) => {
      if (reduceMotion || !heroRef.current) return;
      const r = heroRef.current.getBoundingClientRect();
      heroRef.current.style.setProperty(
        "--px",
        ((e.clientX - r.left) / r.width - 0.5).toFixed(3)
      );
      heroRef.current.style.setProperty(
        "--py",
        ((e.clientY - r.top) / r.height - 0.5).toFixed(3)
      );
    },
    [reduceMotion]
  );

  const onMouseLeave = useCallback(() => {
    heroRef.current?.style.setProperty("--px", 0);
    heroRef.current?.style.setProperty("--py", 0);
  }, []);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    },
    [next, prev]
  );

  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchRef.current == null) return;
    const dx = e.changedTouches[0].clientX - touchRef.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchRef.current = null;
  };

  if (!count) {
    return (
      <section className={styles.hero}>
        <div className={styles.fallback}>
          {t("commons.noSlides", "Aucun slide à afficher")}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={heroRef}
      className={styles.hero}
      role="region"
      aria-roledescription="carrousel"
      aria-label={t("home.heroLabel", "Voyages en promotion")}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      tabIndex={-1}
    >
      {slides.map((slide, i) => {
        const active = i === index;
        /* Repli sur la forme historique quand les champs enrichis manquent. */
        const eyebrow = slide.eyebrow || slide.country;
        const title = slide.title || slide.city;
        const desc = slide.desc || slide.subtitle;

        return (
          <article
            key={slide.id ?? i}
            className={`${styles.slide} ${active ? styles.slideActive : ""}`}
            aria-roledescription="diapositive"
            aria-label={`${i + 1} / ${count}`}
            aria-hidden={!active}
          >
            <div className={styles.media}>
              <img src={slide.img} alt={slide.city || title || ""} decoding="async" />
            </div>
            <div className={styles.overlay} />

            <div className={styles.content}>
              {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}

              <h2 className={styles.title}>
                {title}
                {slide.titleAccent && <> <em>{slide.titleAccent}</em></>}
              </h2>

              {desc && <p className={styles.desc}>{desc}</p>}

              {Array.isArray(slide.chips) && slide.chips.length > 0 && (
                <div className={styles.chips}>
                  {slide.chips.map((c, k) => (
                    <span key={k}>{c}</span>
                  ))}
                </div>
              )}

              {(slide.priceNow || slide.priceOld) && (
                <div className={styles.price}>
                  {slide.priceOld && (
                    <div className={styles.priceOld}>
                      <span>{t("home.insteadOf", "Au lieu de")}</span>
                      <s>{slide.priceOld}</s>
                    </div>
                  )}
                  {slide.priceNow && (
                    <div className={styles.priceNow}>{slide.priceNow}</div>
                  )}
                  {slide.priceNote && (
                    <div className={styles.priceNote}>{slide.priceNote}</div>
                  )}
                  {slide.discount && (
                    <PromoBadge
                      discount={slide.discount}
                      label={t("home.promo", "PROMOTION")}
                    />
                  )}
                </div>
              )}

              <div className={styles.ctaRow}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => onExplore?.(slide)}
                  tabIndex={active ? 0 : -1}
                >
                  {slide.ctaLabel || t("home.discoverOffer", "Découvrir l'offre")}
                  <ArrowRight />
                </button>

                {onSecondary && (
                  <button
                    type="button"
                    className={styles.linkGhost}
                    onClick={() => onSecondary(slide)}
                    tabIndex={active ? 0 : -1}
                  >
                    {slide.secondaryLabel ||
                      t("home.allPromotions", "Voir toutes les promotions")}
                  </button>
                )}
              </div>
            </div>
          </article>
        );
      })}

      {/* « À découvrir ensuite » : les slides suivants, dans l'ordre de
          lecture, en boucle. On en montre trois — au-delà la colonne
          déborderait sur un écran de portable en paysage. */}
      {count > 1 && (
        <div
          className={styles.rail}
          aria-label={t("home.upNext", "Autres destinations à découvrir")}
        >
          <span className={styles.railLabel}>
            {t("home.upNextLabel", "À découvrir ensuite")}
          </span>
          {Array.from({ length: Math.min(3, count - 1) }, (_, k) => {
            const i = (index + k + 1) % count;
            const slide = slides[i];
            return (
              <button
                key={slide.id ?? i}
                type="button"
                className={styles.railItem}
                onClick={() => goTo(i)}
                aria-label={`${t("home.goToSlide", "Aller à la diapositive")} ${
                  i + 1
                } — ${slide.city || slide.title || ""}`}
              >
                <img src={slide.img} alt="" loading="lazy" aria-hidden="true" />
                <span className={styles.railNum}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={styles.railPlace}>
                  {slide.city || slide.title}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.shade} />

      <div className={styles.footer}>
        <div className={styles.indicators}>
          {slides.map((slide, i) => (
            <button
              key={slide.id ?? i}
              type="button"
              className={`${styles.bar} ${i === index ? styles.barActive : ""}`}
              aria-label={t("home.goToSlide", "Aller à la diapositive") + ` ${i + 1}`}
              aria-current={i === index}
              onClick={() => goTo(i)}
            >
              {i === index && <span ref={fillRef} className={styles.barFill} />}
            </button>
          ))}
        </div>

        <div className={styles.footerRight}>
          <p className={styles.counter}>
            <span className={styles.counterCurrent}>
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className={styles.counterSep}>/</span>
            <span className={styles.counterTotal}>
              {String(count).padStart(2, "0")}
            </span>
          </p>

          <button
            type="button"
            className={styles.playToggle}
            onClick={() => setPlaying((p) => !p)}
            aria-label={
              playing
                ? t("home.pause", "Mettre en pause")
                : t("home.play", "Reprendre")
            }
          >
            {playing ? <IconPause /> : <IconPlay />}
          </button>

          <button
            type="button"
            className={styles.arrow}
            onClick={prev}
            aria-label={t("home.previous", "Diapositive précédente")}
          >
            <ChevronLeft />
          </button>

          <button
            type="button"
            className={styles.arrow}
            onClick={next}
            aria-label={t("home.next", "Diapositive suivante")}
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      <p className={styles.srOnly} aria-live="polite">
        {`${index + 1} / ${count}`}
      </p>

      {children}
    </section>
  );
}

HeroCarousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      img: PropTypes.string.isRequired,
      /* forme historique */
      city: PropTypes.string,
      country: PropTypes.string,
      subtitle: PropTypes.string,
      /* champs enrichis, tous optionnels */
      eyebrow: PropTypes.string,
      title: PropTypes.string,
      titleAccent: PropTypes.string,
      desc: PropTypes.string,
      chips: PropTypes.arrayOf(PropTypes.string),
      priceOld: PropTypes.string,
      priceNow: PropTypes.string,
      priceNote: PropTypes.node,
      discount: PropTypes.string,
      ctaLabel: PropTypes.string,
      secondaryLabel: PropTypes.string,
    })
  ).isRequired,
  autoPlayDelay: PropTypes.number,
  onExplore: PropTypes.func,
  onSecondary: PropTypes.func,
  children: PropTypes.node,
};
